import { tokens } from '@env0/ui-theme';
import {
  ANTD_BREAKPOINTS,
  sharedPrimitives,
  type Breakpoints,
  type DesignPrimitives,
  type LayoutDimensions
} from './design-primitives.constants';

// Re-exported so the theme module stays the one place callers look for theme-adjacent constants.
export { ANTD_BREAKPOINTS };
export type { Breakpoints, LayoutDimensions };

export const NAVBAR_WIDTH = tokens.layout.navbarWidth;
export const NAVBAR_COLLAPSED_WIDTH = tokens.layout.navbarCollapsedWidth;
export const NAVBAR_TRANSITION_DURATION = tokens.layout.navbarTransitionDuration;

/** Colours are not on the theme: CSS reads the token variables, `var(--…)`, and JS reads `tokens`. */
export type Env0Theme = DesignPrimitives & {
  mode: 'light' | 'dark';
  breakpoints: Breakpoints;
  layout: LayoutDimensions;
  /**
   * kbar's scrim, the one colour the palette cannot take from a `var()`: `kbar-wrapper.tsx` was on the
   * raw-colour lint allowlist because of it.
   */
  kbarScrim: string;
};

/**
 * The two shell dimensions where the Figma frames and Figma's own `Layout` collection disagree. The
 * collection is what `tokens.layout` mirrors and it still carries the old numbers, so per D7.1 the
 * frames win:
 *
 * - Breadcrumb nav (225:2043) is 38px, not 35. `inner-layout.tsx` and `navbar-header.tsx` both read it,
 *   which keeps the rail header level with the strip (APO-471).
 * - Navigation / Top (225:2025) is 60px, not 50. Nothing reads this any more: the top bar builds its
 *   height from padding (APO-468).
 */
const APP_LAYOUT = { ...tokens.layout, topBarHeight: '60px', breadcrumbBarHeight: '38px' };

const designPrimitives = {
  ...sharedPrimitives,
  breakpoints: ANTD_BREAKPOINTS,
  layout: APP_LAYOUT,
  kbarScrim: tokens.theme.light.surface.main['modal-backdrop']
} as const;

export const themes = {
  light: { mode: 'light', ...designPrimitives },
  dark: { mode: 'dark', ...designPrimitives }
} satisfies Record<'light' | 'dark', Env0Theme>;

export const darkTheme = themes.dark;

declare module '@emotion/react' {
  export interface Theme extends Env0Theme {}
}

export default themes.light;
